import { CurrencyDollarIcon } from "@heroicons/react/24/outline";
import { Formik, Form } from "formik";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import * as Yup from "yup";
import { MoneyInput } from "@/core/components/money-input";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { NewBankDetailsModal } from "@/business-settings/payments/new-bank-details-modal";
import { parseMaskedDollarInput } from "@/core/utils/money";
import {
  useDepositWithdraw,
  useGetAchAccount,
  useGetAccountSummary,
  useGetInternalTransactions,
} from "@/internal-transactions/query";

const schema = Yup.object().shape({
  amount: Yup.number()
    .transform((_, orig) => {
      if (orig === "" || orig === null || orig === undefined) return undefined;
      const parsed = parseMaskedDollarInput(orig);
      return parsed !== null ? parsed : undefined;
    })
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(1, "Amount must be at least 1"),
});

interface PaymentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  businessId?: string;
}

const PaymentModal = ({
  isOpen,
  onOpenChange,
  onComplete,
  businessId,
}: PaymentModalProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [showBankSetup, setShowBankSetup] = useState(false);
  const {
    data: achAccount,
    isLoading: achLoading,
    isFetched: achFetched,
  } = useGetAchAccount(businessId);
  const {
    data: accountSummary,
    isLoading: summaryLoading,
    isFetched: summaryFetched,
  } = useGetAccountSummary(businessId);
  const {
    data: internalTransactions = [],
    isLoading: txLoading,
    isFetched: txFetched,
  } = useGetInternalTransactions(businessId);
  const { mutate: deposit, isPending } = useDepositWithdraw(true, businessId);

  const hasAchAccount = !!achAccount;
  const hasBalance = Number(accountSummary?.wallet_balance ?? 0) > 0;
  const hasTransactions = internalTransactions.length > 0;
  const shouldShowDeposit = hasAchAccount && !hasBalance && !hasTransactions;
  const isLoading = achLoading || summaryLoading || txLoading;
  const isResolved = achFetched && summaryFetched && txFetched;

  const handleClose = () => {
    if (!isPending) {
      setIsSuccess(false);
      setShowBankSetup(false);
      onOpenChange(false);
    }
  };

  const handleComplete = () => {
    setIsSuccess(false);
    onComplete();
  };

  const handleAddBankAccount = () => {
    setShowBankSetup(true);
  };

  if (showBankSetup) {
    return (
      <NewBankDetailsModal
        isOpen={showBankSetup}
        onOpenChange={setShowBankSetup}
        businessId={businessId}
      />
    );
  }

  if (isLoading || !isResolved) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Checking account status…</DialogTitle>
          </DialogHeader>
          <Stack gap="lg" fullWidth>
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-secondary-text" />
            </div>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (!hasAchAccount) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bank Account Required</DialogTitle>
          </DialogHeader>

          <Stack gap="lg" fullWidth>
            <Stack gap="md" align="center" fullWidth className="px-6">
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400" />
              <Typography
                variant="bodyW1"
                className="text-center text-secondary-text"
              >
                You need to add your bank account details before you can deposit
                funds.
              </Typography>
            </Stack>

            <DialogFooter className="w-full">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleAddBankAccount}>Add Bank Account</Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (!shouldShowDeposit) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>No Action Required</DialogTitle>
          </DialogHeader>

          <Stack gap="lg" fullWidth>
            <Stack gap="md" align="center" fullWidth className="px-6">
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400" />
              <Typography
                variant="bodyW1"
                className="text-center text-secondary-text"
              >
                {hasBalance
                  ? "You already have funds in your account."
                  : "You have existing transaction history."}
              </Typography>
            </Stack>

            <DialogFooter className="w-full sm:justify-center">
              <Button onClick={handleComplete}>Continue</Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deposit Successful!</DialogTitle>
          </DialogHeader>

          <Stack gap="lg" fullWidth>
            <Stack gap="md" align="center" fullWidth className="px-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
              <Typography
                variant="bodyW1"
                className="text-center text-secondary-text"
              >
                Your deposit has been initiated successfully. It may take a few
                business days to appear in your account.
              </Typography>
            </Stack>

            <DialogFooter className="w-full sm:justify-center">
              <Button onClick={handleComplete}>Continue</Button>
            </DialogFooter>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Deposit Funds</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={{
            amount: "",
          }}
          validationSchema={schema}
          validateOnMount={true}
          onSubmit={(values, { setSubmitting }) => {
            const parsed = parseMaskedDollarInput(String(values.amount ?? ""));
            const amountCents = parsed !== null ? Math.round(parsed * 100) : 0;

            return deposit(
              { amount: amountCents },
              {
                onSuccess: () => {
                  toast.success("Deposit successfully initiated");
                  setSubmitting(false);
                  setIsSuccess(true);
                },
                onError: () => {
                  toast.error("Failed to initiate deposit. Please try again.");
                  setSubmitting(false);
                },
              },
            );
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
            values,
            setFieldValue,
          }) => (
            <Form>
              <Stack gap="lg" fullWidth>
                <Stack gap="md" fullWidth className="px-6">
                  <Typography variant="bodyW1" className="text-secondary-text">
                    Enter the amount you'd like to deposit into your Volca
                    account.
                  </Typography>

                  <MoneyInput
                    label="Amount"
                    placeholder="0.00"
                    error={touched.amount ? errors.amount : undefined}
                    value={values.amount}
                    onChange={(value) => setFieldValue("amount", value)}
                    name="amount"
                    fullWidth
                  />

                  {achAccount && (
                    <Typography
                      variant="captionW1"
                      className="text-secondary-text"
                    >
                      Funds will be withdrawn from your {achAccount.name}{" "}
                      account ending in {achAccount.mask}
                    </Typography>
                  )}
                </Stack>

                <DialogFooter className="w-full">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isValid}
                    loading={isSubmitting}
                    leftIcon={<CurrencyDollarIcon className="h-4 w-4" />}
                  >
                    Deposit
                  </Button>
                </DialogFooter>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
